import React, {useState, useEffect} from "react";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {makeStyles} from "@material-ui/core/styles";

import formatTrackedChanges from "common_components/report/utils/format_tracked_changes";
import fixLockedTextValue from "common_components/report/utils/fix_locked_text_value";
import constructIssueNameTextValue from "common_components/report/utils/construct_issue_name_text_value";
import getTextFromClipboard from "utils/text/get_text_from_clipboard";
import moveReferencesToTheTextLine from "utils/text/move_references_to_text_line";
import {getUpdatedManualCorrections} from "utils/manual_corrections_utils";
import TextValue from "common_components/report/report_item/text_value";
import extractOriginalTextFromMarkup from "common_components/report/utils/extract_original_text_from_markup";
import Editor from "common_components/editor";

const useStyles = makeStyles({
  root: {
    marginBottom: "20px",
  },
  buttonGroup: {
    display: "flex",
    alignItems: "center",
    marginTop: "8px",
  },
  textField: {
    marginBottom: "0",
    width: "100%",
    marginTop: "0.75em",
  },
  input: {
    borderRadius: 0,
    minHeight: "50px",
    padding: "10px",
    fontSize: "13px",
    fontWeight: 100,
  },
  textFieldBlock: {
    marginBottom: "0.75em",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    padding: "10px",
    minHeight: "50px",
    fontSize: "13px",
    fontWeight: 100,
    backgroundColor: "#f5f5f5",
  },
});

const TextFieldWithStateValue = ({
  issue,
  selectedReportId,
  onSave,
  fieldValue,
  correctDocumentIssueManually,
  lockedTextValue,
  fieldName,
  defaultValue,
  label,
  isMarkdown,
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(fieldValue);
  const [isEditing, setIsEditing] = useState(false);
  const [isTrackingChanges, setIsTrackingChanges] = useState(
    Boolean(lockedTextValue),
  );
  const [currentEditValue, setCurrentEditValue] = useState(fieldValue);
  const [originalValue, setOriginalValue] = useState(
    defaultValue || fieldValue,
  );
  const [lastSavedTrackedValue, setLastSavedTrackedValue] = useState(null);

  useEffect(() => {
    if (!isTrackingChanges) {
      setValue(fieldValue);
      setCurrentEditValue(fieldValue);
      setOriginalValue(fieldValue);
      setLastSavedTrackedValue(null);
    }
  }, [fieldValue, isTrackingChanges]);

  useEffect(() => {
    if (lockedTextValue) {
      const fixedValue = fixLockedTextValue(lockedTextValue);
      setCurrentEditValue(fixedValue);
      setLastSavedTrackedValue(fixedValue);
      setIsTrackingChanges(true);
    }
  }, [lockedTextValue]);

  useEffect(() => {
    if (lockedTextValue) {
      setLastSavedTrackedValue(lockedTextValue);
    }
  }, []);

  const handleChange = e => {
    if (typeof e === "string") {
      setCurrentEditValue(e);
    } else if (e && e.target) {
      setCurrentEditValue(e.target.value);
    }
  };

  const saveWithTrackedChanges = () => {
    correctDocumentIssueManually(issue, {
      manual_corrections: getUpdatedManualCorrections(
        issue.manual_corrections,
        selectedReportId,
        fieldName,
        fieldName === "issue_name"
          ? constructIssueNameTextValue(currentEditValue, issue)
          : currentEditValue,
        false,
        null,
        formatTrackedChanges(currentEditValue, originalValue),
      ),
    });
    setLastSavedTrackedValue(currentEditValue);
    setValue(currentEditValue);
  };

  const saveWithoutTrackedChanges = () => {
    onSave({target: {value: currentEditValue}});
    setValue(currentEditValue);
    setOriginalValue(currentEditValue);
  };

  const handleSave = () => {
    if (isTrackingChanges) {
      saveWithTrackedChanges();
    } else {
      saveWithoutTrackedChanges();
    }
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (isTrackingChanges) {
      if (lastSavedTrackedValue) {
        setCurrentEditValue(lastSavedTrackedValue);
        setIsEditing(false);
      } else {
        setIsTrackingChanges(false);
        setCurrentEditValue(value);
        setIsEditing(false);
      }
    } else {
      setCurrentEditValue(value);
      setIsEditing(false);
    }
  };

  const handleStartTracking = () => {
    setIsTrackingChanges(true);
    setOriginalValue(value);
    setLastSavedTrackedValue(null);
    setCurrentEditValue(value);
    setIsEditing(true);
  };

  const handleAcceptChanges = () => {
    correctDocumentIssueManually(issue, {
      manual_corrections: getUpdatedManualCorrections(
        issue.manual_corrections,
        selectedReportId,
        fieldName,
        currentEditValue,
        null,
        false,
        null,
        "delete",
      ),
    });
    onSave({target: {value: currentEditValue}});
    setValue(currentEditValue);
    setOriginalValue(currentEditValue);
    setLastSavedTrackedValue(null);
    setIsTrackingChanges(false);
  };

  const handleRejectChanges = () => {
    const extractedOriginal = extractOriginalTextFromMarkup(lockedTextValue);

    correctDocumentIssueManually(issue, {
      manual_corrections: getUpdatedManualCorrections(
        issue.manual_corrections,
        selectedReportId,
        fieldName,
        extractedOriginal,
        null,
        false,
        null,
        "revert",
      ),
    });
    setValue(extractedOriginal);
    setCurrentEditValue(extractedOriginal);
    setOriginalValue(extractedOriginal);
    setLastSavedTrackedValue(null);
    setIsTrackingChanges(false);
  };

  const onTextPaste = e => {
    e.preventDefault();
    const pastedText = getTextFromClipboard(e);
    const newTextValue = `${currentEditValue.slice(
      0,
      e.target.selectionStart,
    )}${moveReferencesToTheTextLine(pastedText)}${currentEditValue.slice(
      e.target.selectionEnd,
      currentEditValue.length,
    )}`;
    setCurrentEditValue(newTextValue);
  };

  const hasChanges = currentEditValue !== (lastSavedTrackedValue || value);

  const getFormattedTrackedChanges = (isPreview = false) => {
    const valueToFormat = isPreview
      ? currentEditValue
      : lastSavedTrackedValue || currentEditValue;
    return formatTrackedChanges(valueToFormat, originalValue);
  };

  const renderInput = () => {
    const inputProps = {
      value: currentEditValue,
      onChange: handleChange,
      onPaste: onTextPaste,
      label: `${label}${isTrackingChanges ? " (Tracking Changes)" : ""}`,
    };

    if (isMarkdown) {
      return <Editor {...inputProps} />;
    }

    return (
      <TextField
        {...inputProps}
        multiline
        rows={4}
        variant="outlined"
        className={classes.textField}
        InputProps={{
          classes: {input: classes.input},
        }}
      />
    );
  };

  if (!isTrackingChanges || isEditing) {
    return (
      <div className={classes.root}>
        {renderInput()}
        <div
          className={classes.buttonGroup}
          style={{justifyContent: "space-between"}}
        >
          <div>
            {hasChanges && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSave}
              >
                Save
              </Button>
            )}
          </div>
          <div>
            {isEditing || currentEditValue !== value ? (
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelEditing}
              >
                Cancel
              </Button>
            ) : !isTrackingChanges ? (
              <Button
                variant="outlined"
                size="small"
                onClick={handleStartTracking}
              >
                Track Changes
              </Button>
            ) : null}
          </div>
        </div>
        {isTrackingChanges && (
          <div className={classes.textFieldBlock} style={{marginTop: "10px"}}>
            <TextValue value={getFormattedTrackedChanges(true)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div style={{marginTop: "16px"}}>
        <div style={{marginBottom: "6px"}}>{label}</div>
        <div className={classes.textFieldBlock}>
          <TextValue value={getFormattedTrackedChanges()} />
        </div>
      </div>

      <div
        className={classes.buttonGroup}
        style={{justifyContent: "space-between"}}
      >
        <div>
          <Button variant="outlined" size="small" onClick={handleStartEditing}>
            Edit
          </Button>
        </div>
        <div style={{display: "flex", gap: "8px"}}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleAcceptChanges}
          >
            Accept Changes
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleRejectChanges}
          >
            Reject Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TextFieldWithStateValue;
